import Backdrop from "@components/Backdrop"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { formatContentfulVariant } from "@utils/index"
import { getMarketAmount } from "@utils/pricing"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

import DeviceSelector from "./DeviceSelector"
import Form from "./Form"
import LanguageSelector from "./LanguageSelector"
import SelectProduct from "./SelectProduct"
import SizeSelector from "./SizeSelector"

const openProps = `
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`
export const Container = styled.div<{ $isOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  opacity: 0;
  transform: translateX(100%);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}

  @media ${breakpoints.tablet} {
    max-width: 64%;
  }

  @media ${breakpoints.laptop} {
    max-width: calc(5 / 12 * 100vw);
  }
`

interface ProductSelectorProps {
  addToCart: any
  availableVariants: Array<any>
  closeOnAddToCart?: boolean
  containerRef?: any
  customerCountryCode?: string
  deal: string
  header: string
  isOpen: boolean
  onRequestClose: () => void
  products: Array<any>
  showPromoToast?: boolean
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  addToCart,
  availableVariants,
  closeOnAddToCart,
  containerRef,
  customerCountryCode = "US",
  deal,
  header,
  isOpen,
  onRequestClose,
  products,
  showPromoToast,
}) => {
  const menuRef: any = React.useRef(null)

  const [selectedProduct, setSelectedProduct] = React.useState<any | undefined>(
    undefined
  )

  const handleOnRequestClose = () => {
    setSelectedProduct(undefined)
    onRequestClose()
  }

  useModalBackgroundClose({
    isOpen,
    modalRef: menuRef,
    onRequestClose: handleOnRequestClose,
  })

  const [deviceSelectorIsOpen, setDeviceSelectorIsOpen] = React.useState(false)
  const [deviceSelectorData, setDeviceSelectorData] = React.useState([])

  const openDeviceSelector = (variantDevices) => {
    setDeviceSelectorData(variantDevices)
    setDeviceSelectorIsOpen(true)
  }

  const closeDeviceSelector = () => {
    setDeviceSelectorIsOpen(false)
  }

  const [languageSelectorIsOpen, setLanguageSelectorIsOpen] =
    React.useState(false)
  const [languageSelectorData, setLanguageSelectorData] = React.useState([])

  const openLanguageSelector = (productLanguages) => {
    setLanguageSelectorData(productLanguages)
    setLanguageSelectorIsOpen(true)
  }

  const closeLanguageSelector = () => {
    setLanguageSelectorIsOpen(false)
  }

  const [sizeSelectorIsOpen, setSizeSelectorIsOpen] = React.useState(false)
  const [sizeSelectorData, setSizeSelectorData] = React.useState([])

  const openSizeSelector = (productSizes) => {
    setSizeSelectorData(productSizes)
    setSizeSelectorIsOpen(true)
  }

  const closeSizeSelector = () => {
    setSizeSelectorIsOpen(false)
  }

  const closeForm = () => {
    setSelectedProduct(undefined)
  }

  const { onAddProductToCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart } = useElevarEvents()

  const formatProduct = (p) => {
    const formattedVariants = p.variants.map(formatContentfulVariant)
    const variantsWithClickHandlers = formattedVariants.map((v) => {
      const onAddToCart = (quantity) => {
        let multipleDevices

        const productDevices = p.devices || []

        if (productDevices.length > 0) {
          multipleDevices = productDevices.reduce((acc, curr) => {
            if (acc) {
              acc = acc + `, ${curr?.name}`
            } else {
              acc = curr?.name
            }

            return acc
          }, undefined)
        }

        const imageSrc = _.get(v, "images[0]", "")
        const [cleanedSrc] = imageSrc.split("?")

        let device = multipleDevices ? multipleDevices : p.device?.name

        if (p.customPrint) {
          device = v.deviceName || p.device?.name
        }

        addToCart({
          cartProduct: {
            brand: p.brand?.name,
            builtInMagnet: !!p.builtInMagnet,
            bundleProduct: p.bundleProduct || false,
            color: v.color,
            compareAtPrice: getMarketAmount(
              v,
              customerCountryCode,
              "compareAtPrice"
            ),
            contentfulProductId: p.contentful_id,
            contentfulVariantId: v.contentfulVariantId,
            customPrint: p.customPrint || false,
            device,
            deviceType: p.device?.type?.name,
            image: `https:${cleanedSrc}`,
            language: p.language,
            marketPrices: v.marketPrices,
            price: getMarketAmount(v, customerCountryCode, "price"),
            productName: p.productName,
            productSlug: p.slug,
            productSubType: p.productSubType?.slug,
            quantity,
            shopifyProductId: p.shopifyProduct?.shopifyProductId,
            shopifyVariantId: v.variantId,
            size: v.size,
            sku: v.sku,
            variantDevice: p.customPrint ? v.deviceName : undefined,
            variantName: v.name,
          },
          showPromoToast: showPromoToast || false,
        })

        const elevarProduct: ElevarProductProps = {
          brand: "Urban Armor Gear",
          device,
          deviceType: p.device?.type?.name,
          image: `https:${cleanedSrc}`,
          list: "upsell",
          price: getMarketAmount(v, customerCountryCode, "price"),
          productId: p.shopifyProduct?.shopifyProductId,
          productName: p.productName,
          quantity: String(quantity),
          sku: v.sku,
          variantId: v.variantId,
          variantName: v.name,
        }

        elevarOnAddToCart(elevarProduct, "upsell")

        const gtmProduct: GTMProductProps = {
          brand: p.brand?.name,
          device,
          deviceType: p.device?.type?.name,
          price: getMarketAmount(v, customerCountryCode, "price"),
          productId: p.shopifyProduct?.shopifyProductId,
          productName: p.productName,
          quantity,
          sku: v.sku,
          variantName: v.name,
        }

        onAddProductToCart(gtmProduct)

        if (closeOnAddToCart) {
          onRequestClose()
        }
      }

      return {
        ...v,
        onAddToCart,
      }
    })

    return {
      ...p,
      variants: variantsWithClickHandlers,
    }
  }

  const formatBlocks = (p) => {
    return {
      discountedPrice: getMarketAmount(
        _.get(p, "variants[0]"),
        customerCountryCode,
        "compareAtPrice"
      ),
      image: _.get(p, "variants[0].images[0]", ""),
      onClick: () => {
        setSelectedProduct(p)
      },
      price: getMarketAmount(
        _.get(p, "variants[0]"),
        customerCountryCode,
        "price"
      ),
      productName: p.productName,
    }
  }

  const formattedProducts = React.useMemo(() => {
    return products.map(formatProduct)
  }, [products])

  const formattedBlocks = React.useMemo(() => {
    return formattedProducts.map(formatBlocks)
  }, [formattedProducts])

  React.useEffect(() => {
    if (isOpen && formattedBlocks.length === 1) {
      setSelectedProduct(formattedProducts[0])
    }
  }, [isOpen])

  return (
    <Backdrop $isOpen={isOpen} ref={containerRef}>
      <Container $isOpen={isOpen} ref={menuRef}>
        <SelectProduct
          deal={deal}
          header={header}
          onRequestClose={onRequestClose}
          products={formattedBlocks}
        />
        {selectedProduct && (
          <Form
            availableVariants={availableVariants}
            customPrint={!!selectedProduct.customPrint}
            header={header}
            onClickDeviceSelector={openDeviceSelector}
            onClickLanguageSelector={openLanguageSelector}
            onClickSizeSelector={openSizeSelector}
            onRequestClose={
              formattedBlocks.length === 1 ? onRequestClose : closeForm
            }
            onRequestCloseDeviceSelector={closeDeviceSelector}
            onRequestCloseLanguageSelector={closeLanguageSelector}
            onRequestCloseSizeSelector={closeSizeSelector}
            productName={selectedProduct.productName}
            variants={selectedProduct.variants}
          />
        )}
        <DeviceSelector
          devices={deviceSelectorData}
          isOpen={deviceSelectorIsOpen}
          onRequestClose={closeDeviceSelector}
        />
        <LanguageSelector
          isOpen={languageSelectorIsOpen}
          languages={languageSelectorData}
          onRequestClose={closeLanguageSelector}
        />
        <SizeSelector
          isOpen={sizeSelectorIsOpen}
          onRequestClose={closeSizeSelector}
          sizes={sizeSelectorData}
        />
      </Container>
    </Backdrop>
  )
}

export default ProductSelector
